/**
 * CR310: Deal Card With Details - V2 Only
 * 
 * V1 rendering paths have been completely removed.
 * This component now exclusively uses the V2 renderer with unified presentation.
 * 
 * Supports presentation modes:
 * - Flip: FlipPresentation (v2)
 * - Modal: ModalPresentation (v2)
 * - SidePanel: SidePanelPresentation (v2)
 * - InlineExpand: InlineExpandPresentation (v2)
 * 
 * Emergency rollback: ?dealCardV2=0 shows admin warning instead of V1
 */

import { useCallback, useMemo } from "react";
import { useDealCardUX, getCardHeightClass } from "@/contexts/DealCardUXContext";
import { useDealDetailsOptional } from "@/contexts/DealDetailsContext";
import { usePublishedDealCardConfigContext } from "@/contexts/PublishedDealCardConfigContext";
import { useDealCardV2Override, logDealCardRendererChoice } from "@/lib/deal-cards/v2Override";
import { useIsSpecV2Enabled, useDealCardSpecV2 } from "@/hooks/useDealCardSpecV2";
import { bridgeTemplateToV2Spec, type TemplateOverrideSpec } from "@/lib/deal-cards/templateBridge";
import { DealCardRendererV2 } from "@/components/deal-cards/DealCardRendererV2";
import { DetailsPresentationWrapper } from "@/components/deal-cards/presentation";
import { V2ErrorBoundary } from "@/components/deal-cards/V2ErrorBoundary";
import { ParityCompareView, isCompareModeEnabled } from "@/components/deal-cards/ParityCompareView";
import { getDealType } from "@/lib/deals/getDealType";
import { deriveDetailsPresentationFromLegacy } from "@/lib/layout-studio/detailsPresentationTypes";
import type { Deal } from "@/lib/types/deals";
import type { DetailsPresentationConfig } from "@/lib/layout-studio/detailsPresentationTypes";
import type { FeedbackPlacement } from "@/lib/layout-studio/dealCardUXTypes";
import { AlertTriangle } from "lucide-react";
import { DealFeedbackWidget } from "@/components/deals/DealFeedbackWidget";
import { useDealFeedback } from "@/hooks/useDealFeedback";

interface DealCardWithDetailsProps {
  deal: Deal & { id?: string; dedupe_key?: string };
  isAdmin?: boolean;
  /** Unique identifier for this deal in the list */
  dealId: string;
}

export function DealCardWithDetails({
  deal,
  isAdmin = false,
  dealId,
}: DealCardWithDetailsProps) {
  const { 
    detailsMode, 
    cardSize, 
    expandSingleOpen,
    customHeightsEnabled,
    cardHeightPx,
    detailsPresentation,
    feedbackWidget,
  } = useDealCardUX();
  const detailsContext = useDealDetailsOptional();
  
  // CR310: Get v2 feature flag status (deprecated but kept for emergency rollback)
  const { enabled: featureFlagEnabled } = useIsSpecV2Enabled();
  
  // CR310: Determine v2 override - now always true unless emergency override
  const { useV2, debug: v2Debug } = useDealCardV2Override(featureFlagEnabled, isAdmin);
  
  // Get deal type key
  const { category: dealTypeKey } = getDealType(deal);
  
  // CR310: Always load V2 spec (no skip condition since V1 is removed)
  const { spec: baseSpec, loading: specLoading, error: specError } = useDealCardSpecV2({
    dealTypeKey,
    skip: false,
  });
  
  // CR415: Apply published CR412 config override if context is provided
  const publishedConfigCtx = usePublishedDealCardConfigContext();
  const spec = useMemo(() => {
    if (!baseSpec) return null;
    if (!publishedConfigCtx) return baseSpec;
    const override = publishedConfigCtx.getTemplateOverride(dealTypeKey);
    if (!override) return baseSpec;
    return bridgeTemplateToV2Spec(baseSpec, override as TemplateOverrideSpec);
  }, [baseSpec, publishedConfigCtx, dealTypeKey]);
  
  // Derive presentation config from legacy detailsMode if not set
  const effectivePresentation: DetailsPresentationConfig = useMemo(() => {
    if (detailsPresentation) {
      return detailsPresentation;
    }
    return deriveDetailsPresentationFromLegacy(detailsMode, expandSingleOpen);
  }, [detailsPresentation, detailsMode, expandSingleOpen]);
  
  // Debug log for renderer selection (admin only)
  useMemo(() => {
    if (isAdmin && useV2) {
      logDealCardRendererChoice(useV2, v2Debug, dealTypeKey);
    }
  }, [isAdmin, useV2, v2Debug, dealTypeKey]);
  
  // Height configuration
  const heightClass = customHeightsEnabled ? undefined : getCardHeightClass(cardSize);
  const heightPx = customHeightsEnabled ? cardHeightPx : undefined;
  
  // Handle expand toggle
  const handleExpandToggle = useCallback(() => {
    if (detailsContext) {
      detailsContext.toggleExpand(dealId);
    }
  }, [detailsContext, dealId]);
  
  const isExpanded = detailsContext?.isExpanded(dealId) ?? false;
  const feedbackEnabled = feedbackWidget?.enabled === true;
  const feedbackPlacement: FeedbackPlacement = feedbackWidget?.placement ?? "above_card";
  const dedupeKey = deal.dedupe_key ?? deal.id ?? null;
  const feedback = useDealFeedback({
    dealDedupeKey: dedupeKey,
    dealId: deal.id ?? null,
    source: isAdmin ? "admin_preview" : "site",
    enabled: !isAdmin,
  });
  
  // CR310: Emergency rollback warning (admin only)
  if (!useV2 && isAdmin) {
    return (
      <div className="rounded-lg border-2 border-destructive/50 bg-destructive/10 p-4 min-h-[200px] flex flex-col items-center justify-center gap-2">
        <AlertTriangle className="h-8 w-8 text-destructive" />
        <p className="text-sm font-medium text-destructive">Emergency V1 Rollback Active</p>
        <p className="text-xs text-muted-foreground text-center">
          V1 renderer has been removed. Remove ?dealCardV2=0 to use V2.
        </p>
      </div>
    );
  }
  
  // CR310: Public site emergency rollback - just show loading state
  if (!useV2) {
    return (
      <div className="rounded-lg bg-muted/50 border border-border p-4 min-h-[200px] flex flex-col items-center justify-center gap-2 animate-pulse">
        <div className="h-8 w-8 rounded-full bg-muted" />
        <div className="h-4 w-24 bg-muted rounded" />
      </div>
    );
  }
  
  // Parity compare mode - show V1 + V2 side-by-side (admin only)
  const compareMode = isCompareModeEnabled();
  if (spec && !specLoading && compareMode && isAdmin) {
    return (
      <ParityCompareView
        deal={deal}
        spec={spec}
        isAdmin={isAdmin}
      />
    );
  }
  
  // Loading state
  if (specLoading || !spec) {
    return (
      <div className="rounded-lg bg-muted/30 border border-border p-4 min-h-[200px] flex items-center justify-center animate-pulse">
        <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }
  
  // Spec error state (admin warning)
  if (specError && isAdmin) {
    return (
      <div className="rounded-lg border-2 border-amber-500/50 bg-amber-500/10 p-4 min-h-[200px] flex flex-col items-center justify-center gap-2">
        <AlertTriangle className="h-6 w-6 text-amber-600" />
        <p className="text-sm font-medium text-amber-700">Spec Load Error</p>
        <p className="text-xs text-muted-foreground text-center max-w-[200px]">
          {specError.slice(0, 100)}
        </p>
      </div>
    );
  }
  
  // V2 renderer with error boundary
  const shouldRenderInsideCardFeedback = feedbackEnabled && feedbackPlacement === "inside_card";
  const suppressBlockIds = shouldRenderInsideCardFeedback ? [] : ["feedback"];

  const feedbackConfig = (spec.resolved.components?.feedback ?? {}) as {
    pendingLabel?: string;
    positiveLabel?: string;
    negativeLabel?: string;
  };

  const renderExternalFeedback = () => {
    if (!feedbackEnabled || feedbackPlacement === "inside_card") {
      return null;
    }

    const widget = (
      <DealFeedbackWidget
        pendingLabel={feedbackConfig.pendingLabel}
        positiveLabel={feedbackConfig.positiveLabel}
        negativeLabel={feedbackConfig.negativeLabel}
        value={feedback.value}
        onValueChange={(next) => feedback.submitVote(next)}
      />
    );

    if (feedbackPlacement === "above_card") {
      return <div className="mb-2 flex justify-center">{widget}</div>;
    }
    if (feedbackPlacement === "top_left") {
      return <div className="mb-2 flex justify-start">{widget}</div>;
    }
    if (feedbackPlacement === "top_right") {
      return <div className="mb-2 flex justify-end">{widget}</div>;
    }
    if (feedbackPlacement === "below_left") {
      return <div className="mt-2 flex justify-start">{widget}</div>;
    }
    if (feedbackPlacement === "below_right") {
      return <div className="mt-2 flex justify-end">{widget}</div>;
    }
    return null;
  };

  const v2Element = (
    <V2ErrorBoundary
      dealId={deal.id || deal.dedupe_key}
      dealTypeKey={dealTypeKey}
      isAdmin={isAdmin}
    >
      <DealCardRendererV2
        deal={deal}
        spec={spec}
        isAdmin={isAdmin}
        surface="primary"
        debug={false}
        suppressBlockIds={suppressBlockIds}
      />
    </V2ErrorBoundary>
  );
  
  // Use unified DetailsPresentationWrapper for all modes
  return (
    <V2ErrorBoundary
      dealId={deal.id || deal.dedupe_key}
      dealTypeKey={dealTypeKey}
      isAdmin={isAdmin}
    >
      <div className="space-y-0">
        {(feedbackPlacement === "above_card" || feedbackPlacement === "top_left" || feedbackPlacement === "top_right") && renderExternalFeedback()}
        <DetailsPresentationWrapper
          deal={deal}
          spec={spec}
          presentation={effectivePresentation}
          isAdmin={isAdmin}
          dealId={dealId}
          isExpanded={isExpanded}
          onExpandToggle={handleExpandToggle}
          heightClass={heightClass}
          heightPx={heightPx}
          debug={false}
        >
          {v2Element}
        </DetailsPresentationWrapper>
        {(feedbackPlacement === "below_left" || feedbackPlacement === "below_right") && renderExternalFeedback()}
      </div>
    </V2ErrorBoundary>
  );
}

export default DealCardWithDetails;
